// Path routing on the History API. The server answers every non-/api GET with
// index.html, so each of these paths can be opened directly.
import React, { useEffect, useState } from "react";

/** Settings: the dealer's own (or Phoneware's), Phoneware activity, Sign-in, Your accounts, Connect an AI or app, and Connected apps. */
export type SettingsTab = "general" | "activity" | "signin" | "accounts" | "connect" | "apps";

export type Route =
  | { name: "signin" }
  | { name: "signin-finish" }
  | { name: "today" }
  | { name: "quotes" }
  | { name: "quote-templates" }
  | { name: "quote-new" }
  | { name: "quote"; id: string }
  | { name: "quote-intake"; id: string }
  | { name: "quotelink"; token: string }
  | { name: "orders" }
  | { name: "mac-new" }
  | { name: "order"; id: string }
  | { name: "customers" }
  | { name: "customer"; id: number }
  | { name: "requests" }
  | { name: "request"; id: string }
  | { name: "catalog" }
  | { name: "settings"; tab: SettingsTab }
  | { name: "connect"; requestId: string }
  | { name: "approvals" }
  | { name: "dealers" }
  | { name: "not-found" };

const STATIC: Record<string, Route> = {
  "/": { name: "today" },
  "/signin": { name: "signin" },
  "/signin/finish": { name: "signin-finish" },
  "/quotes": { name: "quotes" },
  "/quotes/new": { name: "quote-new" },
  "/quotes/templates": { name: "quote-templates" },
  "/orders": { name: "orders" },
  "/orders/mac/new": { name: "mac-new" },
  "/customers": { name: "customers" },
  "/catalog": { name: "catalog" },
  "/requests": { name: "requests" },
  "/settings": { name: "settings", tab: "general" },
  "/settings/activity": { name: "settings", tab: "activity" },
  "/settings/signin": { name: "settings", tab: "signin" },
  "/settings/accounts": { name: "settings", tab: "accounts" },
  "/settings/connect": { name: "settings", tab: "connect" },
  "/settings/apps": { name: "settings", tab: "apps" },
  "/approvals": { name: "approvals" },
  "/dealers": { name: "dealers" },
};

export function matchRoute(pathname: string): Route {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const fixed = STATIC[path];
  if (fixed) return fixed;
  const intake = /^\/quotes\/intake\/([^/]+)$/.exec(path);
  if (intake) return { name: "quote-intake", id: decodeURIComponent(intake[1]) };
  const request = /^\/requests\/([^/]+)$/.exec(path);
  if (request) return { name: "request", id: decodeURIComponent(request[1]) };
  const m = /^\/(quotes|q|orders|customers|connect)\/([^/]+)$/.exec(path);
  if (!m) return { name: "not-found" };
  const param = decodeURIComponent(m[2]);
  if (m[1] === "connect") return { name: "connect", requestId: param };
  if (m[1] === "quotes") return { name: "quote", id: param };
  if (m[1] === "q") return { name: "quotelink", token: param };
  if (m[1] === "orders") return { name: "order", id: param };
  const id = Number(param);
  return Number.isInteger(id) && id > 0 ? { name: "customer", id } : { name: "not-found" };
}

const listeners = new Set<() => void>();

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  for (const listener of listeners) listener();
  window.scrollTo(0, 0);
}

/** The current path and query, re-rendering on navigate() and on back and forward. */
export function useLocation(): { path: string; query: URLSearchParams } {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    listeners.add(bump);
    window.addEventListener("popstate", bump);
    return () => {
      listeners.delete(bump);
      window.removeEventListener("popstate", bump);
    };
  }, []);
  return { path: window.location.pathname, query: new URLSearchParams(window.location.search) };
}

type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: string };

/** An in-app link: a real href for new tabs and copying, client-side navigation for a plain click. */
export function Link({ to, onClick, ...rest }: LinkProps): React.ReactElement {
  return (
    <a
      {...rest}
      href={to}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
    />
  );
}

/** A table row that opens a record when clicked anywhere outside its own links and controls. */
export function rowLink(to: string): { "data-go": string; onClick: (e: React.MouseEvent<HTMLElement>) => void } {
  return {
    "data-go": to,
    onClick: (e) => {
      if (e.target instanceof Element && e.target.closest("a, button, input, select, textarea, label")) return;
      navigate(to);
    },
  };
}
